import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  RocketLaunchIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Home = () => {
  const { isAuthenticated } = useAuth();

  const [stats, setStats] = useState([
    { label: 'Projets actifs', value: '—' },
    { label: 'Investisseurs', value: '—' },
    { label: 'Pays couverts', value: '—' },
    { label: 'Montants levés', value: '—' },
  ]);

  useEffect(() => {
    api.get('/stats').then(res => {
      const d = res.data;
      setStats([
        { label: 'Projets actifs', value: d.activeProjects.toLocaleString('fr-FR') },
        { label: 'Investisseurs', value: d.investors.toLocaleString('fr-FR') },
        { label: 'Pays couverts', value: d.countriesCovered.toString() },
        {
          label: 'Montants levés',
          value: d.totalFundingEur >= 1_000_000
            ? `€${(d.totalFundingEur / 1_000_000).toFixed(1)}M`
            : d.totalFundingEur >= 1_000
            ? `€${Math.round(d.totalFundingEur / 1_000)}K`
            : `€${d.totalFundingEur.toLocaleString('fr-FR')}`
        },
      ]);
    }).catch(() => {
      // En cas d'erreur, conserver les tirets (état initial)
    });
  }, []);

  const features = [
    {
      icon: RocketLaunchIcon,
      title: 'Soumettez vos idées',
      description: 'Présentez vos projets innovants et attirez l\'attention des investisseurs du monde entier.',
    },
    {
      icon: UserGroupIcon,
      title: 'Réseau d\'investisseurs',
      description: 'Connectez-vous avec des investisseurs qualifiés et des partenaires stratégiques.',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Financement multi-devises',
      description: 'Gérez vos projets dans différentes devises et pays pour une portée internationale.',
    },
    {
      icon: ChartBarIcon,
      title: 'Analytics avancés',
      description: 'Suivez les performances de vos projets avec des tableaux de bord détaillés.',
    },
    {
      icon: GlobeAltIcon,
      title: 'Portée mondiale',
      description: 'Accédez à un marché international d\'entrepreneurs et d\'investisseurs.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Sécurisé et fiable',
      description: 'Plateforme sécurisée avec des paiements protégés et des données chiffrées.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Bourse d'Idées - Innovactive 360° | Plateforme d'investissement et d'entrepreneuriat</title>
        <meta name="description" content="Connectez entrepreneurs et investisseurs sur notre plateforme internationale. Soumettez vos projets, trouvez des financements et développez votre réseau." />
        <meta name="keywords" content="investissement, entrepreneuriat, startup, financement, innovation, réseau" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative gradient-bg text-white overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Bourse d'<span className="text-yellow-300">Idées</span>
              </h1>
              <p className="text-lg md:text-xl mb-4 text-white/90">
                <span className="font-semibold">Innovactive 360°</span> - Plateforme d'accompagnement
              </p>
              <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
                La plateforme qui connecte entrepreneurs et investisseurs pour transformer les idées en succès
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="btn btn-lg bg-white text-primary-500 hover:bg-gray-100"
                    >
                      Mon Tableau de Bord
                    </Link>
                    <Link
                      to="/projects/create"
                      className="btn btn-lg border-2 border-white text-white hover:bg-white hover:text-primary-500"
                    >
                      Créer un Projet
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="btn btn-lg bg-white text-primary-500 hover:bg-gray-100"
                    >
                      Commencer Gratuitement
                    </Link>
                    <Link
                      to="/login"
                      className="btn btn-lg border-2 border-white text-white hover:bg-white hover:text-primary-500"
                    >
                      Se Connecter
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Floating elements */}
          <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
          <div className="absolute bottom-20 right-10 w-16 h-16 bg-white rounded-full opacity-20 animate-bounce" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-yellow-300 rounded-full opacity-20 animate-bounce" style={{ animationDelay: '2s' }}></div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi choisir Bourse d'Idées ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Une plateforme complète qui facilite la rencontre entre entrepreneurs et investisseurs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="card p-6 hover:shadow-medium transition-shadow duration-300">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <feature.icon className="h-6 w-6 text-primary-500" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 gradient-bg text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Prêt à transformer votre idée en réalité ?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Rejoignez des milliers d'entrepreneurs et d'investisseurs qui font confiance à notre plateforme
            </p>
            {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="btn btn-lg bg-white text-primary-500 hover:bg-gray-100"
                >
                  Créer un Compte Gratuit
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/subscriptions"
                  className="btn btn-lg border-2 border-white text-white hover:bg-white hover:text-primary-500"
                >
                  Voir nos Tarifs
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Comment ça marche ?
              </h2>
              <p className="text-xl text-gray-600">
                Trois étapes simples pour démarrer
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Créez votre profil
                </h3>
                <p className="text-gray-600">
                  Inscrivez-vous gratuitement et complétez votre profil pour attirer l'attention
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Soumettez votre projet
                </h3>
                <p className="text-gray-600">
                  Décrivez votre idée en détail avec notre formulaire optimisé pour les investisseurs
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Connectez-vous
                </h3>
                <p className="text-gray-600">
                  Recevez des contacts d'investisseurs intéressés et développez votre réseau
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
